import { useState } from "react";
import { useAccessControl } from "../hooks/useAccessControl";
import { PricingModal } from "@/ui-components/PricingModal";
import {
  ExclamationTriangleIcon,
  ClockIcon,
} from "@heroicons/react/20/solid";

const SUPPORT_EMAIL = "support@newsquawk.com";

const formatDate = (iso: string | null): string | null => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return null;
  }
};

const formatDateObj = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

/**
 * Contextual banner shown when the user lacks live access.
 *
 * Priority:
 *  1. If `access_expires_at_epoch` exists in the token → source of truth.
 *  2. Otherwise fall back to the subscription record / subscriber role.
 */
export const SubscriptionBanner = () => {
  const {
    accessExpiresAtDate,
    isAccessExpired,
    showSubscriptionBanner,
    subscriptionQuery,
  } = useAccessControl();

  const { data: subscription, isLoading } = subscriptionQuery;
  const [isPricingOpen, setIsPricingOpen] = useState(false);

  // ── Epoch-based access (source of truth when present) ──
  if (accessExpiresAtDate !== null) {
    // Active epoch → no banner
    if (!isAccessExpired) return null;

    // Expired epoch → show expired banner
    return (
      <>
        <PricingModal
          isOpen={isPricingOpen}
          onClose={() => setIsPricingOpen(false)}
        />
        <div className="bg-red-50 dark:bg-red-950/40 border-b border-red-200 dark:border-red-900/50 px-4 py-3">
          <div className="mx-auto flex items-center justify-center gap-4 sm:gap-6">
            <ExclamationTriangleIcon className="hidden sm:block h-5 w-5 shrink-0 text-red-500 dark:text-red-400" />
            <div className="text-center sm:text-left">
              <p className="text-sm font-semibold leading-tight text-red-700 dark:text-red-300">
                Your access expired on {formatDateObj(accessExpiresAtDate)}
              </p>
              <p className="mt-0.5 text-xs text-red-600/80 dark:text-red-400/80">
                Subscribe to a plan or contact{" "}
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="underline hover:text-red-800 dark:hover:text-red-200"
                >
                  {SUPPORT_EMAIL}
                </a>{" "}
                to renew your access.
              </p>
            </div>
            <button
              onClick={() => setIsPricingOpen(true)}
              className="shrink-0 inline-flex items-center px-4 py-1.5 bg-red-600 text-white text-sm font-bold rounded-md hover:bg-red-700 transition-colors shadow-sm cursor-pointer"
            >
              Get Access
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Subscription / role fallback (epoch absent) ──
  if (!showSubscriptionBanner || isLoading) return null;

  const endedOn =
    formatDate(subscription?.cancelled_at ?? null) ??
    formatDate(subscription?.current_period_end ?? null);

  const hasSubscriptionRecord = !!subscription;

  return (
    <>
      <PricingModal
        isOpen={isPricingOpen}
        onClose={() => setIsPricingOpen(false)}
      />
      <div className="bg-red-50 dark:bg-red-950/40 border-b border-red-200 dark:border-red-900/50 px-4 py-3">
        <div className="mx-auto flex items-center justify-center gap-4 sm:gap-6">
          {hasSubscriptionRecord ? (
            <ClockIcon className="hidden sm:block h-5 w-5 shrink-0 text-red-500 dark:text-red-400" />
          ) : (
            <ExclamationTriangleIcon className="hidden sm:block h-5 w-5 shrink-0 text-red-500 dark:text-red-400" />
          )}
          <div className="text-center sm:text-left">
            {hasSubscriptionRecord ? (
              <>
                <p className="text-sm font-semibold leading-tight text-red-700 dark:text-red-300">
                  Your subscription is no longer active
                </p>
                <p className="mt-0.5 text-xs text-red-600/80 dark:text-red-400/80">
                  {endedOn ? `Ended on ${endedOn}. ` : ""}
                  Reactivate your plan or contact{" "}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="underline hover:text-red-800 dark:hover:text-red-200"
                  >
                    {SUPPORT_EMAIL}
                  </a>
                </p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold leading-tight text-red-700 dark:text-red-300">
                  No active subscription
                </p>
                <p className="mt-0.5 text-xs text-red-600/80 dark:text-red-400/80">
                  Subscribe to access real-time market news, analytics, and
                  squawk audio.
                </p>
              </>
            )}
          </div>
          {hasSubscriptionRecord ? (
            <a
              href="https://newsquawk.chargebeeportal.com"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center px-4 py-1.5 bg-red-600 text-white text-sm font-bold rounded-md hover:bg-red-700 transition-colors shadow-sm"
            >
              Reactivate
            </a>
          ) : (
            <button
              onClick={() => setIsPricingOpen(true)}
              className="shrink-0 inline-flex items-center px-4 py-1.5 bg-red-600 text-white text-sm font-bold rounded-md hover:bg-red-700 transition-colors shadow-sm cursor-pointer"
            >
              Get Access
            </button>
          )}
        </div>
      </div>
    </>
  );
};
